import styles from './expandable-text-field.module.css'

import cn from 'clsx'
import * as React from 'react'
import { useCallback, useRef, useState } from 'react'

type Props = {
  value: string
  placeholder?: string
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
}

/**
 * A text field that looks and behaves like a normal single-line TextField
 * until focused, then grows to show all of its content (wrapping instead of
 * clipping) and collapses back to one line on blur. Built on a single
 * <textarea> — rather than swapping between <input> and <textarea> — so
 * focus/selection survive the expand/collapse.
 */
const ExpandableTextField = ({
  value,
  placeholder,
  onChange,
}: Props): React.ReactElement => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  const resize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
    // Wait a frame so the collapsed class is removed (nowrap lifted) before
    // measuring scrollHeight, otherwise it measures the single-line height.
    requestAnimationFrame(resize)
  }, [resize])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    if (textareaRef.current) {
      textareaRef.current.style.height = ''
    }
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') event.preventDefault()
    },
    [],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const el = textareaRef.current
      if (el && /[\r\n]/.test(el.value)) {
        el.value = el.value.replace(/[\r\n]+/g, ' ')
      }
      onChange(event)
      if (isFocused) resize()
    },
    [onChange, isFocused, resize],
  )

  return (
    <div className={cn('TextField', { 'TextField--focused': isFocused })}>
      <div className="TextField-input-wrapper">
        <textarea
          ref={textareaRef}
          className={cn(
            'TextField-input',
            'TextField-input--default',
            styles.textarea,
            { [styles.collapsed]: !isFocused },
          )}
          rows={1}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
    </div>
  )
}

export default ExpandableTextField
